/**
 * Vistas de las pestañas secundarias del workspace (§7): relaciones, temas
 * materiales, materialidad, fuentes, duplicados e historial de corridas.
 *
 * Son listas de solo lectura. Los flujos de curaduría —fusionar, descartar,
 * profundizar— se enchufan en la Fase 6.
 */

import { ExternalLink, Link2, Sparkles } from 'lucide-react'

import {
  useCorridas,
  useFuentes,
  useMaterialidad,
  useProfundizarEntidad,
  useRelaciones,
  useTemas,
} from '../api/hooks'
import type { Corrida } from '../api/tipos'
import { mensajeDeError, useAvisos } from '../ui/avisos-contexto'
import { Boton } from '../ui/primitivos'
import type { Prioridad } from '../api/tipos'
import { COLOR_PRIORIDAD, ETIQUETA_PRIORIDAD, ETIQUETA_RELACION } from '../ui/paleta'
import { useState } from 'react'

import {
  Chip,
  EsqueletoTabla,
  Error as EstadoError,
  Interruptor,
  Vacio,
} from '../ui/primitivos'
import { cx, formatearCosto, formatearFecha, urlExterna } from '../ui/util'

// --------------------------------------------------------------------------
// Relaciones
// --------------------------------------------------------------------------

export function VistaRelaciones({
  entidadId,
  onAbrir,
}: {
  entidadId: number
  onAbrir: (id: number) => void
}) {
  const consulta = useRelaciones(entidadId)

  if (consulta.isLoading) return <EsqueletoTabla filas={6} columnas={4} />
  if (consulta.isError)
    return <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />

  const filas = consulta.data?.results ?? []
  if (!filas.length) {
    return (
      <Vacio
        titulo="Sin relaciones entre stakeholders"
        detalle="Esta investigación no encontró vínculos laterales. Los vínculos de cada actor con la entidad se ven en el grafo como radios."
        icono={<Link2 size={22} />}
      />
    )
  }

  return (
    <table className="w-full border-collapse text-[13px]">
      <thead className="sticky top-0 bg-[var(--color-panel)]">
        <tr className="border-b border-[var(--color-borde)]">
          {['Origen', 'Relación', 'Destino', 'Descripción'].map((h) => (
            <th
              key={h}
              className="px-3 py-2 text-left text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((r) => (
          <tr key={r.id} className="border-b border-[var(--color-borde)]">
            <td className="px-3 py-2">
              <button
                onClick={() => onAbrir(r.origen)}
                className="text-left font-medium hover:text-[var(--color-acento)] hover:underline"
              >
                {r.origen_nombre}
              </button>
            </td>
            <td className="px-3 py-2">
              <Chip>{ETIQUETA_RELACION[r.tipo] ?? r.tipo}</Chip>
            </td>
            <td className="px-3 py-2">
              <button
                onClick={() => onAbrir(r.destino)}
                className="text-left font-medium hover:text-[var(--color-acento)] hover:underline"
              >
                {r.destino_nombre}
              </button>
            </td>
            <td className="px-3 py-2 text-[var(--color-texto-suave)]">
              {r.descripcion || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// --------------------------------------------------------------------------
// Temas y materialidad
// --------------------------------------------------------------------------

export function VistaTemas({
  entidadId,
  onAbrir,
  onCorridaLanzada,
}: {
  entidadId: number
  onAbrir: (id: number) => void
  onCorridaLanzada?: (corrida: Corrida) => void
}) {
  const avisos = useAvisos()
  const temas = useTemas(entidadId)
  const materialidad = useMaterialidad(entidadId)
  const profundizar = useProfundizarEntidad(entidadId)

  function profundizarTema(tema: string) {
    profundizar.mutate(
      { tema },
      {
        onSuccess: (corrida) => {
          if (corrida.reutilizada) {
            avisos.info('Ya había una profundización reciente sobre este tema; se reutilizó.')
          } else {
            avisos.exito(`Profundizando «${tema}»`, {
              detalle: 'Pide solo información nueva. El resultado se integra al grafo solo.',
            })
          }
          onCorridaLanzada?.(corrida)
        },
        onError: (error) => avisos.error('No se pudo profundizar', mensajeDeError(error)),
      },
    )
  }

  if (temas.isLoading || materialidad.isLoading)
    return <EsqueletoTabla filas={5} columnas={3} />
  if (temas.isError)
    return <EstadoError error={temas.error} reintentar={() => temas.refetch()} />

  const listaTemas = temas.data ?? []
  const listaMaterialidad = materialidad.data ?? []

  if (!listaTemas.length && !listaMaterialidad.length) {
    return <Vacio titulo="Sin temas materiales" detalle="La corrida no identificó temas." />
  }

  const porPrioridad = (['critica', 'alta', 'media', 'baja'] as Prioridad[])
    .map((p) => [p, listaMaterialidad.filter((m) => m.prioridad === p)] as const)
    .filter(([, entradas]) => entradas.length > 0)

  return (
    <div className="grid gap-4 p-3 lg:grid-cols-2">
      <div>
        <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase">
          Temas materiales ({listaTemas.length})
        </h3>
        <ul className="grid gap-2">
          {listaTemas.map((t) => (
            <li
              key={t.id}
              className="rounded-md border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-2.5"
            >
              <p className="text-[13px] font-medium">{t.tema}</p>
              {t.descripcion && (
                <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-texto-suave)]">
                  {t.descripcion}
                </p>
              )}
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] text-[var(--color-texto-tenue)]">
                  {t.stakeholders_relacionados.length > 0
                    ? `${t.stakeholders_relacionados.length} stakeholders relacionados`
                    : 'Sin stakeholders vinculados'}
                </span>
                <Boton
                  variante="sutil"
                  icono={<Sparkles size={12} />}
                  cargando={profundizar.isPending && profundizar.variables?.tema === t.tema}
                  onClick={() => profundizarTema(t.tema)}
                  title="Buscar información nueva sobre este tema"
                >
                  Profundizar
                </Boton>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase">
          Matriz de materialidad ({listaMaterialidad.length})
        </h3>
        <div className="grid gap-3">
          {porPrioridad.map(([prioridad, entradas]) => (
            <div key={prioridad}>
              <p
                className="mb-1 text-[11px] font-semibold"
                style={{ color: COLOR_PRIORIDAD[prioridad] }}
              >
                {ETIQUETA_PRIORIDAD[prioridad]} · {entradas.length}
              </p>
              <ul className="grid gap-1.5">
                {entradas.map((m) => (
                  <li
                    key={m.id}
                    className="rounded-md border-l-2 bg-[var(--color-lienzo)] py-1.5 pr-2 pl-2.5"
                    style={{ borderColor: COLOR_PRIORIDAD[prioridad] }}
                  >
                    {m.stakeholder ? (
                      <button
                        onClick={() => onAbrir(m.stakeholder!)}
                        className="text-left text-[13px] font-medium hover:text-[var(--color-acento)] hover:underline"
                      >
                        {m.stakeholder_nombre}
                      </button>
                    ) : (
                      <span className="text-[13px] font-medium">{m.stakeholder_nombre}</span>
                    )}
                    {m.razon && (
                      <p className="mt-0.5 text-[12px] text-[var(--color-texto-suave)]">
                        {m.razon}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// Fuentes
// --------------------------------------------------------------------------

export function VistaFuentes({ entidadId }: { entidadId: number }) {
  const [soloCitadas, setSoloCitadas] = useState(true)
  const consulta = useFuentes(entidadId, soloCitadas)

  const filas = consulta.data?.results ?? []

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-[var(--color-borde)] px-3 py-2">
        <Interruptor activo={soloCitadas} onChange={setSoloCitadas}>
          Solo fuentes citadas
        </Interruptor>
        <span className="text-[11px] text-[var(--color-texto-tenue)]">
          Las no citadas salen de los resultados de búsqueda: evidencia disponible que el modelo
          no usó.
        </span>
      </div>

      <div className="scroll-sutil min-h-0 flex-1 overflow-y-auto">
        {consulta.isLoading ? (
          <EsqueletoTabla filas={8} columnas={3} />
        ) : consulta.isError ? (
          <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />
        ) : !filas.length ? (
          <Vacio titulo="Sin fuentes" detalle="La corrida no registró fuentes." />
        ) : (
          <ul className="divide-y divide-[var(--color-borde)]">
            {filas.map((f) => (
              <li key={f.id} className="px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    {urlExterna(f.url) ? (
                      <a
                        href={urlExterna(f.url)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-start gap-1 text-[13px] font-medium text-[var(--color-acento)] hover:underline"
                      >
                        <span className="break-words">{f.titulo || f.url}</span>
                        <ExternalLink size={11} className="mt-1 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-[13px] font-medium">{f.titulo}</span>
                    )}
                    <p className="mt-0.5 text-[11px] text-[var(--color-texto-tenue)]">
                      {[f.publicador, f.fecha].filter(Boolean).join(' · ') || 'Sin metadatos'}
                    </p>
                    {f.uso_principal && (
                      <p className="mt-1 text-[12px] text-[var(--color-texto-suave)]">
                        {f.uso_principal}
                      </p>
                    )}
                  </div>
                  {!f.citada && <Chip titulo="No citada por el modelo">disponible</Chip>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// Historial de corridas
// --------------------------------------------------------------------------

const COLOR_ESTADO: Record<string, string> = {
  OK: 'var(--color-favorable)',
  PARCIAL: 'var(--color-critica)',
  ERROR: 'var(--color-opositora)',
  EN_CURSO: 'var(--color-acento)',
  PENDIENTE: 'var(--color-texto-tenue)',
}

export function VistaCorridas({ entidadId }: { entidadId: number }) {
  const consulta = useCorridas(entidadId)

  if (consulta.isLoading) return <EsqueletoTabla filas={5} columnas={6} />
  if (consulta.isError)
    return <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />

  const filas = consulta.data?.results ?? []
  if (!filas.length) return <Vacio titulo="Sin corridas registradas" />

  const total = filas.reduce((suma, c) => suma + (c.costo_usd ?? 0), 0)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-[var(--color-borde)] px-3 py-2 text-[12px] text-[var(--color-texto-suave)]">
        {filas.length} corridas · costo acumulado{' '}
        <span className="tabular font-medium">{formatearCosto(total)}</span>
      </div>
      <div className="scroll-sutil min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sticky top-0 bg-[var(--color-panel)]">
            <tr className="border-b border-[var(--color-borde)]">
              {['Fecha', 'Tipo', 'Objetivo', 'Estado', 'Duración', 'Costo', 'Modelo'].map(
                (h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {filas.map((c) => (
              <tr key={c.id} className="border-b border-[var(--color-borde)] align-top">
                <td className="tabular px-3 py-2 whitespace-nowrap">
                  {formatearFecha(c.creado_en)}
                </td>
                <td className="px-3 py-2">
                  <Chip>{c.tipo.toLowerCase()}</Chip>
                </td>
                <td className="px-3 py-2 text-[var(--color-texto-suave)]">
                  {c.stakeholder_objetivo_nombre ||
                    c.categoria_objetivo ||
                    c.tema_objetivo ||
                    '—'}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-flex items-center gap-1.5 whitespace-nowrap"
                    style={{ color: COLOR_ESTADO[c.estado] }}
                  >
                    <span className="size-1.5 rounded-full bg-current" />
                    {c.estado}
                  </span>
                  {c.error && (
                    <p className="mt-0.5 max-w-xs text-[11px] break-words text-[var(--color-opositora)]">
                      {c.error}
                    </p>
                  )}
                </td>
                <td className="tabular px-3 py-2 whitespace-nowrap">
                  {c.duracion_segundos ? `${Math.round(c.duracion_segundos)} s` : '—'}
                </td>
                <td
                  className={cx(
                    'tabular px-3 py-2 whitespace-nowrap',
                    c.costo_usd ? '' : 'text-[var(--color-texto-tenue)]',
                  )}
                >
                  {formatearCosto(c.costo_usd)}
                </td>
                <td className="px-3 py-2 text-[11px] text-[var(--color-texto-tenue)]">
                  {c.modelo || c.provider}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
